import React, { useState, useRef, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import toast from "react-hot-toast";
import Select from "react-select";
import moment from "moment";
import momentTz from "moment-timezone";
// import { FaList, FaCalendar } from "react-icons/fa";

import teamBadgePlaceholder from "../../assets/images/team_badge_placeholder.svg";
import ClubbieModal from "../../components/Modal/ClubbieModal";
import ClubbieInput from "../../components/ClubbieInput/ClubbieInput";
import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import PencilIcon from "../../components/Icons/PencilIcon";
import CalendarIcon from "../../components/Icons/CalendarIcon";
import ListIcon from "../../components/Icons/ListIcon";
import DownloadIcon from "../../components/Icons/DownloadIcon";
import { updateFixture } from "../../redux/slices/tournamentSlice";
import {
  FixtureCalendarView as FixturesCalendarView,
  FixtureListView as FixturesListView,
} from "../fixtures";

// Legacy Fixtures views and CSS were removed during refactor.

const Fixtures = () => {
  const dispatch = useDispatch();
  const { selectedTournament, selectedCategory } = useSelector(
    (state) => state.tournament
  );
  const [showConflicts, setShowConflicts] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [currentDateIndex, setCurrentDateIndex] = useState(0);
  const [viewMode, setViewMode] = useState("list"); // "list" or "calendar"
  const filterRef = useRef(null);
  const [filters, setFilters] = useState({
    phases: [],
    groups: [],
    teams: [],
    pitches: [],
    categories: [],
  });

  // Add state for edit fixture modal
  const [isEditFixtureModal, setIsEditFixtureModal] = useState(false);
  const [selectedFixture, setSelectedFixture] = useState(null);
  const [fixtureFormData, setFixtureFormData] = useState({
    matchDate: "",
    matchTime: "",
    pitch: "",
    referees: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilters(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Find team in season teams
  const findTeamInSeason = (teamId) => {
    if (!teamId || !selectedCategory?.teams) return null;
    return selectedCategory.teams.find((team) => team._id === teamId);
  };

  // Get unique values for filters
  const filterOptions = React.useMemo(() => {
    if (!selectedCategory?.phases)
      return { phases: [], groups: [], teams: [], pitches: [], categories: [] };

    const options = {
      phases: new Set(),
      groups: new Set(),
      teams: new Set(),
      pitches: new Set(),
      categories: new Set(),
    };

    selectedCategory.phases.forEach((phase) => {
      options.phases.add(phase.name);
      phase.blocks.forEach((group) => {
        options.groups.add(group.name);
        group.fixtures.forEach((fixture) => {
          options.pitches.add(fixture.pitch);
        });
      });
    });

    // Add teams from season teams
    selectedCategory.teams?.forEach((team) => {
      options.teams.add(team.teamId.title);
    });

    return {
      phases: Array.from(options.phases).sort(),
      groups: Array.from(options.groups).sort(),
      teams: Array.from(options.teams).sort(),
      pitches: Array.from(options.pitches).sort(),
      categories: Array.from(options.categories).sort(),
    };
  }, [selectedCategory]);

  // Get all fixtures from all groups in all phases
  const allFixtures = React.useMemo(() => {
    if (!selectedCategory?.phases) return [];

    return selectedCategory.phases
      .flatMap((phase, phaseIndex) =>
        phase.blocks.flatMap((group) =>
          group.fixtures.map((fixture) => ({
            ...fixture,
            phaseName: phase.name,
            phaseIndex,
            phaseId: phase._id,
            groupName: group.name,
            blockId: group._id,
            tournamentId: selectedTournament.id,
            categoryId: selectedCategory._id,
          }))
        )
      )
      .filter((fixture) => {
        // Apply filters
        if (
          filters.phases.length &&
          !filters.phases.includes(fixture.phaseName)
        )
          return false;
        if (
          filters.groups.length &&
          !filters.groups.includes(fixture.groupName)
        )
          return false;
        if (filters.pitches.length && !filters.pitches.includes(fixture.pitch))
          return false;
        // Check team filters
        if (filters.teams.length) {
          const homeTeam = findTeamInSeason(fixture.homeTeamId);
          const awayTeam = findTeamInSeason(fixture.awayTeamId);
          const homeTeamName = homeTeam?.teamId?.title;
          const awayTeamName = awayTeam?.teamId?.title;

          if (
            !filters.teams.includes(homeTeamName) &&
            !filters.teams.includes(awayTeamName)
          ) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        // First sort by phase index
        if (a.phaseIndex !== b.phaseIndex) {
          return a.phaseIndex - b.phaseIndex;
        }
        // Then by the UTC instant on matchTime
        const aMs = a.matchTime ? new Date(a.matchTime).valueOf() : 0;
        const bMs = b.matchTime ? new Date(b.matchTime).valueOf() : 0;
        return aMs - bMs;
      });
  }, [selectedCategory, filters]);

  // Handle filter changes
  const handleFilterChange = (filterType, value, checked) => {
    setFilters((prev) => ({
      ...prev,
      [filterType]: checked
        ? [...prev[filterType], value]
        : prev[filterType].filter((v) => v !== value),
    }));
  };

  // Render filter controls
  const renderFilters = () => (
    <div
      className="filter-dropdown light_primary_card p-3 rounded shadow"
      style={{ minWidth: "1000px", maxHeight: "400px", overflowY: "auto" }}
    >
      <div className="row">
        <div className="col-md-2">
          <h6 className="text-[#FEFCEB] mb-2">Phases</h6>
          <div className="d-flex flex-column gap-2">
            {filterOptions.phases.map((phase) => (
              <div key={phase} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`phase-${phase}`}
                  checked={filters.phases.includes(phase)}
                  onChange={(e) =>
                    handleFilterChange("phases", phase, e.target.checked)
                  }
                />
                <label
                  className="form-check-label text-[#FEFCEB]"
                  htmlFor={`phase-${phase}`}
                >
                  {phase}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-2">
          <h6 className="text-[#FEFCEB] mb-2">Groups</h6>
          <div className="d-flex flex-column gap-2">
            {filterOptions.groups.map((group) => (
              <div key={group} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`group-${group}`}
                  checked={filters.groups.includes(group)}
                  onChange={(e) =>
                    handleFilterChange("groups", group, e.target.checked)
                  }
                />
                <label
                  className="form-check-label text-[#FEFCEB]"
                  htmlFor={`group-${group}`}
                >
                  {group}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-2">
          <h6 className="text-[#FEFCEB] mb-2">Pitches</h6>
          <div className="d-flex flex-column gap-2">
            {filterOptions.pitches.map((pitch) => (
              <div key={pitch} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`pitch-${pitch}`}
                  checked={filters.pitches.includes(pitch)}
                  onChange={(e) =>
                    handleFilterChange("pitches", pitch, e.target.checked)
                  }
                />
                <label
                  className="form-check-label text-[#FEFCEB]"
                  htmlFor={`pitch-${pitch}`}
                >
                  {pitch}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-3">
          <h6 className="text-[#FEFCEB] mb-2">Teams</h6>
          <div className="d-flex flex-column gap-2">
            {filterOptions.teams.map((team) => (
              <div key={team} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`team-${team}`}
                  checked={filters.teams.includes(team)}
                  onChange={(e) =>
                    handleFilterChange("teams", team, e.target.checked)
                  }
                />
                <label
                  className="form-check-label text-[#FEFCEB]"
                  htmlFor={`team-${team}`}
                >
                  {team}
                </label>
              </div>
            ))}
          </div>
        </div>
        <div className="col-md-3">
          <h6 className="text-[#FEFCEB] mb-2">Categories</h6>
          <div className="d-flex flex-column gap-2">
            {filterOptions.categories.map((category) => (
              <div key={category} className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id={`category-${category}`}
                  checked={filters.categories.includes(category)}
                  onChange={(e) =>
                    handleFilterChange("categories", category, e.target.checked)
                  }
                />
                <label
                  className="form-check-label text-[#FEFCEB]"
                  htmlFor={`category-${category}`}
                >
                  {category}
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  // Get active filters count
  const getActiveFiltersCount = () => {
    return Object.values(filters).reduce(
      (count, filter) => count + filter.length,
      0
    );
  };

  // Check for pitch conflicts
  const getPitchConflicts = React.useMemo(() => {
    const conflicts = new Set();

    // Group fixtures by their UTC instant
    const fixturesByDateTime = allFixtures.reduce((acc, fixture) => {
      const key = fixture.matchTime ? new Date(fixture.matchTime).toISOString() : 'unknown';
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(fixture);
      return acc;
    }, {});

    // Check for conflicts in each date-time group
    Object.values(fixturesByDateTime).forEach((fixtures) => {
      const pitches = new Set();
      fixtures.forEach((fixture) => {
        if (pitches.has(fixture.pitch)) {
          // Found a conflict
          fixtures.forEach((f) => conflicts.add(f._id));
        } else {
          pitches.add(fixture.pitch);
        }
      });
    });

    return conflicts;
  }, [allFixtures]);

  // Group fixtures by viewer-local calendar day (derived from UTC matchTime)
  const fixturesByDate = React.useMemo(() => {
    if (!allFixtures.length) return {};

    return allFixtures.reduce((acc, fixture) => {
      if (!fixture.matchTime) return acc;
      const date = moment(fixture.matchTime).format('YYYY-MM-DD');
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(fixture);
      return acc;
    }, {});
  }, [allFixtures]);

  // Get sorted dates array
  const sortedDates = React.useMemo(() => {
    return Object.keys(fixturesByDate).sort(
      (a, b) => moment(a).valueOf() - moment(b).valueOf()
    );
  }, [fixturesByDate]);

  // Update getMatchTeams to use new format
  const getMatchTeams = (fixture) => {
    if (!fixture) return null;

    const homeTeam = findTeamInSeason(fixture.homeTeamId);
    const awayTeam = findTeamInSeason(fixture.awayTeamId);

    return {
      homeTeam: {
        title: homeTeam?.teamId?.title || "TBD",
        crest: homeTeam?.teamId?.crest || teamBadgePlaceholder,
      },
      awayTeam: {
        title: awayTeam?.teamId?.title || "TBD",
        crest: awayTeam?.teamId?.crest || teamBadgePlaceholder,
      },
      time: fixture.matchTime ? moment(fixture.matchTime).format("HH:mm") : "",
      pitch: fixture.pitch,
      phaseName: fixture.phaseName,
      groupName: fixture.groupName,
      hasConflict: showConflicts && getPitchConflicts.has(fixture._id),
    };
  };

  // Handle edit fixture
  const handleEditFixture = async () => {
    if (!selectedFixture) return;

    setIsLoading(true);
    const loadingToast = toast.loading("Updating fixture...");

    try {
      // Format referees array to match expected format
      const formattedReferees = fixtureFormData.referees.map((refereeId) => ({
        userId: refereeId,
      }));

      // Combine local date+time pair into a true UTC instant.
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const matchTimeUtc = (fixtureFormData.matchDate && fixtureFormData.matchTime)
        ? momentTz.tz(
            `${fixtureFormData.matchDate} ${fixtureFormData.matchTime}`,
            'YYYY-MM-DD HH:mm',
            tz
          ).toISOString()
        : undefined;

      await dispatch(
        updateFixture({
          tournamentId: selectedTournament.id,
          categoryId: selectedCategory._id,
          phaseId: selectedFixture.phaseId,
          blockId: selectedFixture.blockId,
          fixtureId: selectedFixture._id,
          fixtureData: {
            ...(matchTimeUtc ? { matchTime: matchTimeUtc } : {}),
            pitch: fixtureFormData.pitch,
            referees: formattedReferees,
          },
        })
      ).unwrap();

      toast.dismiss(loadingToast);
      toast.success("Fixture updated successfully");
      setIsEditFixtureModal(false);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.message || "Error updating fixture");
    } finally {
      setIsLoading(false);
    }
  };

  // Get available referees from tournament members
  const getAvailableReferees = () => {
    return selectedTournament?.members?.filter(
      (m) => m.role === "referee"
    ) || [];
  };

  // Add download fixtures function
  const downloadFixturesCSV = () => {
    // Create CSV header
    const headers = [
      "Starting time",
      "End time (optional)",
      "Day",
      "Playing field",
      "Phase",
      "Division",
      "Group",
      "Team 1",
      "Team 2",
    ];

    // Create CSV rows
    const rows = Object.entries(fixturesByDate).flatMap(([date, fixtures]) =>
      fixtures.map((fixture) => {
        const homeTeam = findTeamInSeason(fixture.homeTeamId);
        const awayTeam = findTeamInSeason(fixture.awayTeamId);

        // Find the phase to get match rules
        const phase = selectedCategory?.phases?.find(
          (p) => p.name === fixture.phaseName
        );
        const matchRules = phase?.matchRules;

        // Calculate total match duration in minutes
        const totalMatchDuration = matchRules
          ? matchRules.gameDuration * matchRules.breaks +
            (matchRules.breaks - 1) * matchRules.breakDuration
          : 45; // fallback to 45 minutes if no rules found

        // matchTime is a UTC ISO instant — render in viewer-local TZ.
        const startMoment = fixture.matchTime ? moment(fixture.matchTime) : null;
        const endMoment = startMoment ? startMoment.clone().add(totalMatchDuration, "minutes") : null;

        return [
          startMoment ? startMoment.format("HH:mm") : "",
          endMoment ? endMoment.format("HH:mm") : "",
          startMoment ? startMoment.format("DD-MM-YYYY") : "",
          fixture.pitch,
          fixture.phaseName,
          "",
          fixture.groupName,
          homeTeam?.teamId?.title || "TBD",
          awayTeam?.teamId?.title || "TBD",
        ];
      })
    );

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create and trigger download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${selectedTournament.name}_${moment().format("YYYY-MM-DD")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="container">
      <div className="mt-5">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex gap-2">
            <button
              className={`btn ${showConflicts ? "btn-primary" : "btn-outline-primary"}`}
              onClick={() => setShowConflicts(!showConflicts)}
            >
              {showConflicts ? "Hide" : "Show"} Conflicts
            </button>
            <div className="position-relative" ref={filterRef}>
              <button
                className={`btn ${getActiveFiltersCount() > 0 ? "btn-primary" : "btn-outline-light"}`}
                onClick={() => setShowFilters(!showFilters)}
              >
                Filters{" "}
                {getActiveFiltersCount() > 0 && `(${getActiveFiltersCount()})`}
              </button>
              {showFilters && (
                <div
                  className="position-absolute"
                  style={{
                    top: "100%",
                    left: -180,
                    zIndex: 1000,
                    marginTop: "0.5rem",
                  }}
                >
                  {renderFilters()}
                </div>
              )}
            </div>
            <button
              className="btn btn-outline-light"
              onClick={() =>
                setFilters({
                  phases: [],
                  groups: [],
                  teams: [],
                  pitches: [],
                  categories: [],
                })
              }
            >
              Clear Filters
            </button>
            <button
              className="btn btn-outline-light"
              onClick={downloadFixturesCSV}
              title="Download Fixtures CSV"
            >
              <DownloadIcon width={20} height={20} />
            </button>
          </div>
          <div className="d-flex gap-2">
            <button
              className={`btn ${viewMode === "list" ? "btn-primary" : "btn-outline-light"}`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              <ListIcon width={24} height={24} />
            </button>
            <button
              className={`btn ${viewMode === "calendar" ? "btn-primary" : "btn-outline-light"}`}
              onClick={() => setViewMode("calendar")}
              title="Calendar View"
            >
              <CalendarIcon width={24} height={24} />
            </button>
          </div>
        </div>
        {viewMode === "list" ? (
          <FixturesListView
            fixturesByDate={fixturesByDate}
            sortedDates={sortedDates}
            showConflicts={showConflicts}
            startDate={null}
            endDate={null}
            onDateChange={() => {}}
            onShiftDates={() => {}}
            isCalendarView={false}
            onToggleView={() => setViewMode("calendar")}
            onOpenFilters={() => setShowFilters(true)}
            activeFiltersCount={getActiveFiltersCount()}
            selectedTournament={selectedTournament}
            onEditFixture={() => {}}
            onDeleteFixture={() => {}}
            onAddResult={() => {}}
            onEditResult={() => {}}
            onGenerateFixtures={() => {}}
            onCreateFixture={() => {}}
          />
        ) : (
          <FixturesCalendarView
            fixturesByDate={fixturesByDate}
            sortedDates={sortedDates}
            showConflicts={showConflicts}
            selectedTournament={selectedTournament}
            selectedCategory={selectedCategory}
          />
        )}

        {/* Edit Fixture Modal */}
        <ClubbieModal
          isOpen={isEditFixtureModal}
          toggle={() => setIsEditFixtureModal(false)}
          title="Edit Fixture"
        >
          <div className="p-3">
            <div className="mb-3">
              <label className="form-label text-[#FEFCEB]">Match Date</label>
              <ClubbieInput
                type="date"
                value={fixtureFormData.matchDate}
                onChange={(e) =>
                  setFixtureFormData({
                    ...fixtureFormData,
                    matchDate: e.target.value,
                  })
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label text-[#FEFCEB]">Match Time</label>
              <ClubbieInput
                type="time"
                value={fixtureFormData.matchTime}
                onChange={(e) =>
                  setFixtureFormData({
                    ...fixtureFormData,
                    matchTime: e.target.value,
                  })
                }
              />
            </div>
            <div className="mb-3">
              <label className="form-label text-[#FEFCEB]">Pitch</label>
              <select
                className="form-select bg-dark text-[#FEFCEB]"
                value={fixtureFormData.pitch}
                onChange={(e) =>
                  setFixtureFormData({
                    ...fixtureFormData,
                    pitch: e.target.value,
                  })
                }
              >
                <option value="">Select a pitch</option>
                {filterOptions.pitches.map((pitch) => (
                  <option key={pitch} value={pitch}>
                    {pitch}
                  </option>
                ))}
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label text-[#FEFCEB]">Referees</label>
              <Select
                isMulti
                options={getAvailableReferees().map((referee) => ({
                  value: referee.userId.id,
                  label: referee.userId.profile?.fullName || referee.userId.id,
                }))}
                value={fixtureFormData.referees.map((refereeId) => {
                  const refereeData = getAvailableReferees().find(
                    (r) => r.userId.id === refereeId
                  );
                  return {
                    value: refereeId,
                    label:
                      refereeData?.userId?.profile?.fullName ||
                      refereeData?.userId?.id ||
                      "Unknown Referee",
                  };
                })}
                onChange={(selectedOptions) => {
                  const updatedReferees = selectedOptions.map(
                    (option) => option.value
                  );
                  setFixtureFormData({
                    ...fixtureFormData,
                    referees: updatedReferees,
                  });
                }}
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({
                    ...base,
                    backgroundColor: "var(--BLUE-SHADE-7)",
                    borderColor: "var(--BLUE-SHADE-5)",
                    color: "white",
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: "var(--BLUE-SHADE-7)",
                    zIndex: 9999,
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected
                      ? "var(--BLUE-SHADE-5)"
                      : state.isFocused
                        ? "var(--BLUE-SHADE-6)"
                        : "transparent",
                    color: "white",
                    ":active": {
                      backgroundColor: "var(--BLUE-SHADE-5)",
                    },
                  }),
                  multiValue: (base) => ({
                    ...base,
                    backgroundColor: "var(--BLUE-SHADE-5)",
                  }),
                  multiValueLabel: (base) => ({
                    ...base,
                    color: "white",
                  }),
                  multiValueRemove: (base) => ({
                    ...base,
                    color: "white",
                    ":hover": {
                      backgroundColor: "var(--BLUE-SHADE-4)",
                      color: "white",
                    },
                  }),
                }}
              />
            </div>
            <div className="d-flex justify-content-end mt-4">
              <ClubbieButton
                className="me-2"
                onClick={() => setIsEditFixtureModal(false)}
              >
                Cancel
              </ClubbieButton>
              <ClubbieButton onClick={handleEditFixture} disabled={isLoading}>
                {isLoading ? "Updating..." : "Update Fixture"}
              </ClubbieButton>
            </div>
          </div>
        </ClubbieModal>
      </div>
    </div>
  );
};

export default Fixtures;
